<template>
  <div>
    <!-- Warning Alert -->
    <McAlert
      class="mb-3 gap-0 align-items-md-center mc-user-modal__password-alert"
      variant="warning"
      :html="$t('master.user.setPassword.cautionText')"
      iconVariant="fill"
      withIcon
    />

    <!-- Title and Description -->
    <div class="mb-4">
      <h5 class="text-dark fw-bold">
        {{ $t('master.user.setPassword.title') }}
      </h5>
      <p class="text-muted mb-3">
        {{ $t('master.user.setPassword.description') }}
      </p>
    </div>

    <b-form-group>
      <!-- WhatsApp Option -->
      <div
        v-if="form.phoneNo"
        class="mc-user-modal__password-method-card mb-3"
        :class="{
          'mc-user-modal__password-method-card--selected':
            form.setVerification === 'phoneNumber' && form.phoneNo,
          'mc-user-modal__password-method-card--disabled': !form.phoneNo,
        }"
        @click="form.phoneNo && updateVerificationMethod('phoneNumber')"
      >
        <div class="d-flex align-items-center justify-content-between p-3">
          <div class="d-flex align-items-center">
            <b-form-radio
              v-model="verificationValue"
              plain
              value="phoneNumber"
              class="me-3"
              :disabled="!form.phoneNo"
              @change="updateVerificationMethod('phoneNumber')"
            />
            <i
              class="icon-outline_whatsapp me-3 mc-user-modal__icon-whatsapp"
              :class="{ 'mc-user-modal__icon--muted': !form.phoneNo }"
            />
            <span :class="{ 'text-muted': !form.phoneNo }">
              {{ $t('master.user.whatsappTo') }}
            </span>
          </div>
          <span :class="{ 'text-muted': !form.phoneNo }">
            {{ form.phoneNo ? `+${form.phoneNo}` : '-' }}
          </span>
        </div>
      </div>
      <!-- Email Option -->
      <div
        v-if="form.email"
        class="mc-user-modal__password-method-card mb-3"
        :class="{
          'mc-user-modal__password-method-card--selected':
            form.setVerification === 'email' && form.email,
          'mc-user-modal__password-method-card--disabled': !form.email,
        }"
        @click="form.email && updatePasswordMethod('email')"
      >
        <div class="d-flex align-items-center justify-content-between p-3">
          <div class="d-flex align-items-center">
            <b-form-radio
              v-model="verificationValue"
              plain
              value="email"
              class="me-3"
              :disabled="!form.email"
              @change="updatePasswordMethod('email')"
            />
            <i
              class="icon-outline_email me-3 mc-user-modal__icon-email"
              :class="{ 'mc-user-modal__icon--muted': !form.email }"
            />
            <span :class="{ 'text-muted': !form.email }">
              {{ $t('master.user.emailTo') }}
            </span>
          </div>
          <span :class="{ 'text-muted': !form.email }">
            {{ form.email || '-' }}
          </span>
        </div>
      </div>

      <!-- Direct Password Setup Option -->
      <div
        v-if="form.isDriver"
        class="mc-user-modal__password-method-card"
        :class="{
          'mc-user-modal__password-method-card--selected': form.setVerification === 'direct',
        }"
        @click="updatePasswordMethod('direct')"
      >
        <div class="p-3">
          <div class="d-flex align-items-center">
            <b-form-radio
              v-model="verificationValue"
              plain
              value="direct"
              class="me-3"
              @change="updatePasswordMethod('direct')"
            />
            <i class="icon-outline_lock me-3 mc-user-modal__icon-lock" />
            <span>{{ $t('master.user.directSet') }}</span>
          </div>

          <!-- Password Fields - Show when direct is selected -->
          <div v-if="form.setVerification === 'direct'" class="ms-4 mt-3">
            <PasswordFields
              :form="form"
              :show-password="showPassword"
              :show-repassword="showRepassword"
              :show-tooltip-password="showTooltipPassword"
              :show-tooltip-repassword="showTooltipRepassword"
              :password-validations-cases="passwordValidationsCases"
              :is-password-matched="isPasswordMatched"
              @toggle-password="togglePassword"
              @toggle-repassword="toggleRepassword"
              @show-tooltip="onShowTooltip"
              @hide-tooltip="onHideTooltip"
              @update-form="updateForm"
            />
          </div>
        </div>
      </div>
    </b-form-group>
  </div>
</template>

<script>
import PasswordFields from '../fields/PasswordFields.vue'
import McAlert from '@/components/McAlert.vue'

export default {
  name: 'SetPasswordForm',

  components: {
    McAlert,
    PasswordFields,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showPassword: false,
      showRepassword: false,
      showTooltipPassword: false,
      showTooltipRepassword: false,
      passwordValidationsCases: [
        'lowerCase',
        'specialCharacter',
        'upperCase',
        'minimumLength',
        'number',
      ],
    }
  },

  computed: {
    isPasswordMatched() {
      return (
        this.form.password && this.form.repassword && this.form.password === this.form.repassword
      )
    },

    verificationValue: {
      get() {
        return this.form.setVerification
      },
      set(val) {
        this.$emit('update-form', {
          ...this.form,
          setVerification: val,
        })
      },
    },
  },

  methods: {
    updatePasswordMethod(method) {
      this.$emit('update-form', { setVerification: method })
    },

    updateForm(updates) {
      this.$emit('update-form', updates)
    },

    togglePassword() {
      this.showPassword = !this.showPassword
    },

    toggleRepassword() {
      this.showRepassword = !this.showRepassword
    },

    onShowTooltip({ key }) {
      if (key === 'Password') {
        this.showTooltipPassword = true
      } else if (key === 'Repassword') {
        this.showTooltipRepassword = true
      }
    },

    onHideTooltip({ key }) {
      if (key === 'Password') {
        this.showTooltipPassword = false
      } else if (key === 'Repassword') {
        this.showTooltipRepassword = false
      }
    },
  },
}
</script>
